import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '../interfaces/IError';
import { getPreference, putPreference } from '@/appLogic/preferenceManage';
import { getOrganizationId } from '@/utils/token';
import { IBusinessExcelInfosRepository } from '../interfaces/IBusinessExcelInfosRepository';
import { BusinessExcelInfo } from '@/coreDomainsModule/entities/businessExcelInfo';

/**
 * 在庫表エクセルの読み込み情報を管理する
 */
export class BusinessExcelInfosRepository implements IBusinessExcelInfosRepository {
  private myPreferences;

  /**
   * 在庫表エクセルの読み込み情報を取得する
   */
  async get(): Promise<INetworkResult<[string, Record<string, BusinessExcelInfo>], IError>> {
    const businessExcelInfos = await getPreference(await getOrganizationId(), 'businessExcelInfos');

    if (businessExcelInfos != null) {
      return { status: { type: 'success', data: businessExcelInfos } };
    } else {
      return {
        // preference使ってる場合、これしかありえない
        status: {
          type: 'error',
          error: {
            statusCode: 500,
            code: 'notExist',
            message: '在庫表エクセルの読み込み情報が存在しません',
          },
        },
      };
    }
  }

  /**
   * 利用可能な在庫表エクセルのキーのリストを取得する
   */
  async getKeys(): Promise<INetworkResult<string[] | null, IError>> {
    const businessExcelInfos = await getPreference(await getOrganizationId(), 'businessExcelInfos');
    if (businessExcelInfos != null) {
      return {
        status: {
          type: 'success',
          data: Object.keys(businessExcelInfos[1]),
        },
      };
    } else {
      return {
        status: {
          type: 'success',
          data: null,
        },
      };
    }
  }

  /**
   * 在庫表エクセルの読み込み情報を登録する
   * @param targetKeyName - 変更するキー名
   * @param registerKeyName - 登録するキー名
   * @param data - データ
   */
  async put(targetKeyName: string, registerKeyName: string, data: BusinessExcelInfo): Promise<INetworkResult<any, IError>> {
    let myPreferences;
    const businessExcelInfos = await getPreference(await getOrganizationId(), 'businessExcelInfos'); // NOTE: 一旦組織単位で保存する
    let isFirstRegister = false;
    if (businessExcelInfos != null) {
      myPreferences = businessExcelInfos;
      isFirstRegister = false;
    } else {
      isFirstRegister = true;
      myPreferences = [
        [registerKeyName], // こいつを初期設定にする
        {
          [registerKeyName]: undefined,
        },
      ]; // 初期化
    }
    const useKey = isFirstRegister ? registerKeyName : targetKeyName;
    if (myPreferences[1][registerKeyName] != null && targetKeyName !== registerKeyName) {
      // 更新後のキーが既に存在する場合はエラー（keyNameが変わっている場合のみ）
      return {
        status: {
          type: 'error',
          error: {
            statusCode: 400,
            code: 'alreadyExist',
            message: '在庫表エクセルの読み込み情報が既に存在します',
          },
        },
      };
    }
    if (isFirstRegister) {
      myPreferences[0] = useKey;
      myPreferences[1][useKey] = data;
    } else {
      myPreferences[0] = registerKeyName;
      myPreferences[1][registerKeyName] = data; // 新しい方を追加

      if (targetKeyName !== registerKeyName) {
        // キー名が変わっている場合は、古い方を消して新しい方を追加する
        delete myPreferences[1][targetKeyName];
      }
    }
    await putPreference(await getOrganizationId(), 'businessExcelInfos', myPreferences);
    return { status: { type: 'success', data: undefined } };
  }

  /**
   * 取り込みで用いるエクセルのキーを登録する
   * @param key - キー
   */
  async putKey(key: string): Promise<INetworkResult<any, IError>> {
    let myPreferences;

    const businessExcelInfos = await getPreference(await getOrganizationId(), 'businessExcelInfos'); // NOTE: 一旦組織単位で保存する
    if (businessExcelInfos != null) {
      myPreferences = businessExcelInfos;
    } else {
      // ありえない
      return {
        status: {
          type: 'error',
          error: {
            statusCode: 400,
            code: 'notExist',
            message: '在庫表エクセルの読み込み情報が存在しません',
          },
        },
      };
    }
    businessExcelInfos[0] = key;
    await putPreference(await getOrganizationId(), 'businessExcelInfos', myPreferences);
    return { status: { type: 'success', data: undefined } };
  }
}
